import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { notification } from 'antd'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { PageHeader } from '@/components/ui/page-header'
import { deleteCounter, fetchCounters, updateCounterStatus } from '@/api/counters.api'
import type { Counter } from '@/types/api'
import { buildColumns } from './columns'
import { CounterFilterPanel } from './CounterFilterPanel'
import type { CounterFilterValues } from './CounterFilterPanel'
import { CounterDetailDrawer } from './CounterDrawer'
import { CounterFormDrawer } from './CounterFormDrawer'
import { currentUserAtom } from '@/store/auth.atom'
import { useAtomValue } from 'jotai'
import { usePermission } from '@/hooks/use-permission'
import { formatEndDate, formatStartDate } from '@/utils/format'
import { exportCounters, useTableExport } from '@/api/dowload-excel.api'

const DEFAULT_FILTER: CounterFilterValues = {
  deskNumber: undefined,
  name: '',
  unitId: '',
  diagramId: '',
  status: undefined,
  time: undefined,
}

export function CountersPage() {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 })

  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1
  const canCreate = usePermission('reception-create')
  const canView = usePermission('reception-view')
  const canUpdate = usePermission('reception-update')
  const canDelete = usePermission('reception-delete')
  const canExport = usePermission('reception-export')

  const [showFilter, setShowFilter] = useState(false)
  const [filter, setFilter] = useState<CounterFilterValues>(DEFAULT_FILTER)
  const [activeFilters, setActiveFilters] = useState<CounterFilterValues>(DEFAULT_FILTER)
  const [isExporting, setIsExporting] = useState(false)
  const [viewingCounter, setViewingCounter] = useState<Counter | null>(null)
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'update'>('create')
  const [editingCounterId, setEditingCounterId] = useState<string | null>(null)
  const [deletingCounter, setDeletingCounter] = useState<Counter | null>(null)
  const [togglingCounter, setTogglingCounter] = useState<Counter | null>(null)

  const queryClient = useQueryClient()

  const filterParams = useMemo(() => {
    const fromDate = activeFilters.time?.from ? formatStartDate(activeFilters.time.from) : undefined
    const toDateRaw = activeFilters.time?.to || activeFilters.time?.from
    const toDate = toDateRaw ? formatEndDate(toDateRaw) : undefined

    return {
      page: pageIndex,
      limit: pageSize,
      deskNumber: activeFilters.deskNumber || undefined,
      name: activeFilters.name || undefined,
      unitId: showUnitColumn ? activeFilters.unitId : currentUser?.unitId,
      diagramId: activeFilters.diagramId || undefined,
      status: activeFilters.status,
      fromDate,
      toDate,
    }
  }, [pageIndex, pageSize, activeFilters, showUnitColumn, currentUser?.unitId])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['counters', filterParams],
    queryFn: ({ signal }) =>
      fetchCounters(
        filterParams.page,
        filterParams.limit,
        {
          deskNumber: filterParams.deskNumber,
          name: filterParams.name,
          unitId: filterParams.unitId,
          diagramId: filterParams.diagramId,
          status: filterParams.status,
          fromDate: filterParams.fromDate,
          toDate: filterParams.toDate,
        },
        signal,
      ),
    refetchOnMount: 'always',
    staleTime: 0,
    gcTime: 0,
  })
  const hasData = Boolean(data?.items && data.items.length > 0)

  const { mutate: toggleCounterStatus, isPending: isToggling } = useMutation({
    mutationFn: (counter: Counter) => updateCounterStatus(counter, counter.status === 1 ? 0 : 1),
    onSuccess: (response: any) => {
      notification.success({ message: response.responseMessage || 'Cập nhật trạng thái quầy thành công!' })
      queryClient.invalidateQueries({ queryKey: ['counters'] })
      queryClient.invalidateQueries({ queryKey: ['remote-infinite-select', 'counter-desk'] })
    },
    onSettled: () => setTogglingCounter(null),
  })

  const { mutate: removeCounter, isPending: isRemoving } = useMutation({
    mutationFn: (counter: Counter) => deleteCounter(counter.id),
    onSuccess: (response: any) => {
      notification.success({ message: response.responseMessage || 'Xóa quầy thành công!' })
      queryClient.invalidateQueries({ queryKey: ['counters'] })
      queryClient.invalidateQueries({ queryKey: ['remote-infinite-select', 'counter-desk'] })
    },
    onSettled: () => setDeletingCounter(null),
  })

  const columns = useMemo(
    () =>
      buildColumns({
        pageIndex,
        pageSize,
        showUnitColumn,
        canView,
        canUpdate,
        canDelete,
        onViewDetail: (counter) => setViewingCounter(counter),
        onUpdate: (counter) => {
          setFormMode('update')
          setEditingCounterId(counter.id)
          setIsFormDrawerOpen(true)
        },
        onDelete: (counter) => setDeletingCounter(counter),
        onToggleStatus: (counter) => setTogglingCounter(counter),
      }),
    [pageIndex, pageSize, showUnitColumn, canView, canUpdate, canDelete],
  )

  const rows = useMemo(() => data?.items ?? [], [data?.items])

  const { handleExport } = useTableExport<Counter>(
    'Danh sách quầy',
    columns,
    async (payload) => {
          try {
            setIsExporting(true)
            await exportCounters(filterParams, payload, 'danh-sach-quay.xlsx')
          } finally {
            setIsExporting(false)
          }
        },
  )

  if (isLoading) {
    return (
      <section className="space-y-4">
        <PageHeader title="DANH SÁCH QUẦY" isLoading />
        <div className="mt-6 text-center">Đang tải...</div>
      </section>
    )
  }

  return (
    <section className="space-y-4">
      <PageHeader
        title="DANH SÁCH QUẦY"
        onToggleFilter={() => setShowFilter((v) => !v)}
        onExport={canExport ? handleExport : undefined}
        disabled={!hasData}
        isExporting={isExporting}
      >
        {canCreate && (
          <Button
            variant="primary"
            size="md"
            className="flex-1 gap-2 h-10"
            onClick={() => {
              setFormMode('create')
              setEditingCounterId(null)
              setIsFormDrawerOpen(true)
            }}
          >
            <Plus size={16} />
            Thêm mới
          </Button>
        )}
      </PageHeader>

      {showFilter && (
        <CounterFilterPanel
          values={filter}
          onChange={setFilter}
          showUnitColumn={showUnitColumn}
          onSearch={() => {
            const trimmedFilter = {
              ...filter,
              name: filter.name.trim(),
            }
            setFilter(trimmedFilter)
            setActiveFilters(trimmedFilter)
            setPagination((s) => ({ ...s, pageIndex: 0 }))
            refetch()
          }}
        />
      )}

      <DataTable
        columns={columns}
        data={rows}
        tableOptions={{
          state: { pagination: { pageIndex, pageSize } },
          onPaginationChange: setPagination,
          getPaginationRowModel: getPaginationRowModel(),
          getCoreRowModel: getCoreRowModel(),
          manualPagination: true,
          pageCount: data?.totalPages,
        }}
        footer={
          <Pagination
            page={pageIndex + 1}
            pageCount={data?.totalPages ?? 1}
            pageSize={pageSize}
            totalCount={data?.total ?? 0}
            onPageChange={(p) => setPagination((s) => ({ ...s, pageIndex: p - 1 }))}
            onPageSizeChange={(size) => setPagination({ pageIndex: 0, pageSize: size })}
          />
        }
      />

      <CounterDetailDrawer
        counterId={viewingCounter?.id || null}
        showUnitColumn={showUnitColumn}
        onClose={() => setViewingCounter(null)}
        onUpdate={(counter) => {
          setViewingCounter(null)
          setFormMode('update')
          setEditingCounterId(counter.id)
          setIsFormDrawerOpen(true)
        }}
      />

      {isFormDrawerOpen && (
        <CounterFormDrawer
          isOpen={isFormDrawerOpen}
          mode={formMode}
          counterId={editingCounterId}
          onClose={() => {
            setIsFormDrawerOpen(false)
            setEditingCounterId(null)
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!togglingCounter}
        title="Xác nhận"
        description={<>Bạn chắc chắn muốn {togglingCounter?.status === 1 ? 'vô hiệu hóa' : 'kích hoạt'} quầy <strong>{togglingCounter?.name}</strong> này?</>}
        variant="info"
        isLoading={isToggling}
        onClose={() => setTogglingCounter(null)}
        onConfirm={() => togglingCounter && toggleCounterStatus(togglingCounter)}
      />

      <ConfirmModal
        isOpen={!!deletingCounter}
        title="Xóa quầy"
        description={<>Bạn có chắc chắn muốn xóa quầy <strong>{deletingCounter?.name}</strong> này?</>}
        variant="danger"
        isLoading={isRemoving}
        onClose={() => setDeletingCounter(null)}
        onConfirm={() => deletingCounter && removeCounter(deletingCounter)}
      />
    </section>
  )
}
